import {makeObservable, observable, runInAction} from 'mobx';
import {ServiceBase} from '../ServiceBase';
import {IServiceFactoryExtended} from '../service-factory-extended.interface';
import {IClientSettingsService} from './client-settings.service.interface';
import {OrbitUserFlowAnalyticsAction, OrbitUserFlowAnalyticsCategory, OrbitUserFlowAnalyticsLabel} from '../analytics/analytics.service.interface';
import {ClientSettingsComponent} from '../../components/header/top-header-right/client-settings/client-settings.component';
import {ILanguageSwitcherItem} from '../language/language-switcher/language-switcher-item.interface';
import {IClientSettingsDropdownList, IClientSettingsDropdownListItem} from './client-settings-dropdown.interface';
import {BiometricLoginRenderer} from './renderers/biometric-login.renderer';
import {IClientSettingsSectionRendererModel} from './renderers/section-renderer-model.interface';
import {LanguageSwitcherRenderer} from './renderers/language-switcher.renderer';

export class ClientSettingsService extends ServiceBase implements IClientSettingsService {

    constructor(services: IServiceFactoryExtended) {
        super(services);
        makeObservable(this);

        this._loadSections()
            .then(sections => {
                runInAction(() => {
                    this.sections = sections
                })
            })

        this.reaction(
            () => [this.services.language.languageSwitcher.isEmpty, this.services.user.isAuthorized],
            async () => {
                this.sections = await this._loadSections();
            }
        );
    }

    @observable.ref
    private _isOpen: boolean = false;
    get isOpen(): boolean {
        return this._isOpen;
    }

    set isOpen(value: boolean) {
        runInAction(() => {
            this._isOpen = value;
        })
    }

    @observable.ref
    private _sections: IClientSettingsSectionRendererModel[] = [];
    get sections(): IClientSettingsSectionRendererModel[] {
        return this._sections;
    }

    set sections(value: IClientSettingsSectionRendererModel[]) {
        runInAction(() => {
            this._sections = value;
        })
    }


    get needsOverlay(): boolean {
        return this.isOpen;
    }

    @observable.ref
    private _zIndex = 0;
    get zIndex(): number {
        return this._zIndex;
    }


    get isEmpty(): boolean {
        return !Boolean(this.sections.length)
    }

    get languageSwitcherDropdownLists(): IClientSettingsDropdownList[] {
        const dropdownLists: IClientSettingsDropdownList[] = [];
        const languages = this.services.language.languageSwitcher.languagesSection.items;
        const countries = this.services.language.languageSwitcher.countriesSection.items;

        if (languages.length) {
            dropdownLists.push({
                title: this.services.language.languageSwitcher.languagesSection.title,
                items: this._convertLanguageSwitcherItemsToDropdownlist(languages)
            })
        }

        if (countries.length) {
            dropdownLists.push({
                title: this.services.language.languageSwitcher.countriesSection.title,
                items: this._convertLanguageSwitcherItemsToDropdownlist(countries)
            })
        }

        return dropdownLists;
    }

    get currentLanguageOrCountry(): ILanguageSwitcherItem | null {
        const languages = this.services.language.languageSwitcher.languagesSection.items;
        const countries = this.services.language.languageSwitcher.countriesSection.items;

        const currentLanguage = languages.find(language => language.isCurrent);
        if (currentLanguage) {
            return currentLanguage
        };

        const currentCountry = countries.find(country => country.isCurrent);
        if (currentCountry) {
            return currentCountry
        }

        return null
    }

    sendOrbitUserFlowAnalytics(action: OrbitUserFlowAnalyticsAction, label?: OrbitUserFlowAnalyticsLabel | string): void {
        this.services.analytics.sendOrbitUserFlowEvent(OrbitUserFlowAnalyticsCategory.Settings, action, label);
    }

    getOverlayZIndex(): number | undefined {
        return this.zIndex - 1;
    }

    onOverlayClicked(): void {
        this.toggleClientSettingsOpen();
    }

    toggleClientSettingsOpen(): void {
        if (this.isOpen) {
            this._closeClientSettings();
        } else {
            this._openClientSettings();
        }
    }

    async closeClientSettings() {
        this.isOpen = false;
        const drawer = await this.services.drawer;
        drawer.right.closeDrawer();
    }

    async _openClientSettings() {

        this.isOpen = true;
        const drawer = await this.services.drawer;
        drawer.right.push({
            onOverlayClicked: this.toggleClientSettingsOpen.bind(this),
            render: ClientSettingsComponent
        });
    }

    _convertLanguageSwitcherItemsToDropdownlist(items: ILanguageSwitcherItem[]): IClientSettingsDropdownListItem[] {
        const list = items.map(item => ({
            title: item.title,
            isCurrent: item.isCurrent,
            onSelect: () => item.makeCurrent()
        }));
        return list;
    }

    _closeClientSettings() {
        this.closeClientSettings();
    }

    async _loadSections() {
        const sections: IClientSettingsSectionRendererModel[] = [];
        sections.push(new LanguageSwitcherRenderer(this.services));
        sections.push(new BiometricLoginRenderer(this.services));

        const availableSections: IClientSettingsSectionRendererModel[] = [];
        await Promise.all(sections.map(async section => {
            const isSectionAvailable = await section.isAvailable();
            if (isSectionAvailable) {
                return section;
            };

            return null;
        })).then(results => {
            results.forEach(section => {
                if (section) {
                    availableSections.push(section);
                }
            });
        });

        return availableSections;
    }
}
